import React, { useState, useEffect } from 'react';
import { 
  Save, Edit, Eye, Plus, Trash2, X, Upload, Image, Users, Target, Award, CheckCircle,
  Globe, Zap, Code, Smartphone, Palette, Database, Cloud, Shield, Settings, Mail, Phone, MapPin,
  ShoppingBag, Star, Download, Headphones, RefreshCw, MessageCircle, AlertCircle, Clock,
  Calendar, Bell, ArrowRight, ArrowLeft, DollarSign, Send, ShoppingCart
} from 'lucide-react';
import { portfolioPageAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const PortfolioPageManagement = () => {
  const { notify } = useNotificationHook();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [portfolioData, setPortfolioData] = useState({
    main_title: 'Our',
    gradient_title: 'Portfolio',
    main_description: 'Showcasing successful projects that drive real business results',
    stats_projects_completed: 24,
    stats_happy_clients: 5000,
    stats_success_rate: 98.0,
    stats_client_rating: 4.8,
    features_title: 'Our Success Process',
    features_description: 'A proven methodology that ensures project success and client satisfaction',
    features_items: [],
    more_title: 'Ready to Start Your Project?',
    more_description: 'Let\'s discuss your project requirements and create something amazing together',
    more_button1_text: 'Start Your Project',
    more_button1_link: '/contact',
    more_button2_text: 'Get Free Quote',
    more_button2_link: '/quote'
  });

  const [activeSection, setActiveSection] = useState('main');
  const [isEditing, setIsEditing] = useState(false);

  const sections = [
    { id: 'main', label: 'Main Section', icon: Target },
    { id: 'stats', label: 'Statistics', icon: Award },
    { id: 'features', label: 'Features', icon: CheckCircle },
    { id: 'more', label: 'More Section', icon: Globe }
  ];

  // Fetch portfolio page data
  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      const response = await portfolioPageAPI.getContent();
      
      if (response?.data?.success) {
        const data = response.data.data;
        setPortfolioData({
          main_title: data.main_title || 'Our',
          gradient_title: data.gradient_title || 'Portfolio',
          main_description: data.main_description || 'Showcasing successful projects that drive real business results',
          stats_projects_completed: data.stats_projects_completed || 24,
          stats_happy_clients: data.stats_happy_clients || 5000,
          stats_success_rate: data.stats_success_rate || 98.0,
          stats_client_rating: data.stats_client_rating || 4.8,
          features_title: data.features_title || 'Our Success Process',
          features_description: data.features_description || 'A proven methodology that ensures project success and client satisfaction',
          features_items: data.features_items || [],
          more_title: data.more_title || 'Ready to Start Your Project?',
          more_description: data.more_description || 'Let\'s discuss your project requirements and create something amazing together',
          more_button1_text: data.more_button1_text || 'Start Your Project',
          more_button1_link: data.more_button1_link || '/contact',
          more_button2_text: data.more_button2_text || 'Get Free Quote',
          more_button2_link: data.more_button2_link || '/quote'
        });
      }
    } catch (err) {
      console.error('Error fetching portfolio data:', err);
      setError(`Error loading portfolio data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPortfolioData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...portfolioData[field]];
    newArray[index] = value;
    setPortfolioData(prev => ({
      ...prev,
      [field]: newArray
    }));
  };

  const addArrayItem = (field, defaultValue = {}) => {
    setPortfolioData(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field, index) => {
    if (window.confirm('Are you sure you want to delete this feature? This action cannot be undone.')) {
      const newArray = portfolioData[field].filter((_, i) => i !== index);
      setPortfolioData(prev => ({
        ...prev,
        [field]: newArray
      }));
    }
  };

  const savePortfolioData = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await portfolioPageAPI.updateContent(portfolioData);
      
      if (response?.data?.success) {
        notify.success('Portfolio page content updated successfully!');
        setIsEditing(false);
      } else {
        setError('Failed to update portfolio page content');
      }
    } catch (err) {
      console.error('Error saving portfolio data:', err);
      setError(`Error saving portfolio data: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderMainSection = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Main Section</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Main Title
            </label>
            <input
              type="text"
              value={portfolioData.main_title}
              onChange={(e) => handleInputChange('main_title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Our"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Gradient Title
            </label>
            <input
              type="text"
              value={portfolioData.gradient_title}
              onChange={(e) => handleInputChange('gradient_title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Portfolio"
            />
          </div>
        </div>
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={portfolioData.main_description}
            onChange={(e) => handleInputChange('main_description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Showcasing successful projects that drive real business results"
          />
        </div>
      </div>
    </div>
  );

  const renderStatsSection = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Projects Completed
            </label>
            <input
              type="number"
              value={portfolioData.stats_projects_completed}
              onChange={(e) => handleInputChange('stats_projects_completed', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="24"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Happy Clients
            </label>
            <input
              type="number"
              value={portfolioData.stats_happy_clients}
              onChange={(e) => handleInputChange('stats_happy_clients', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="5000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Success Rate (%)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={portfolioData.stats_success_rate}
              onChange={(e) => handleInputChange('stats_success_rate', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="98.0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Client Rating
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="5"
              value={portfolioData.stats_client_rating}
              onChange={(e) => handleInputChange('stats_client_rating', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="4.8"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderFeaturesSection = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Features Section</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Features Title
            </label>
            <input
              type="text"
              value={portfolioData.features_title}
              onChange={(e) => handleInputChange('features_title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Our Success Process"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Features Description
            </label>
            <textarea
              value={portfolioData.features_description}
              onChange={(e) => handleInputChange('features_description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="A proven methodology that ensures project success and client satisfaction"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-md font-medium text-gray-900 dark:text-white">Feature Items</h4>
            <button
              onClick={() => addArrayItem('features_items', { title: '', description: '', icon: 'Target' })}
              className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Feature
            </button>
          </div>

          {portfolioData.features_items.map((feature, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Feature {index + 1}</h5>
                <button
                  onClick={() => removeArrayItem('features_items', index)}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={feature.title}
                    onChange={(e) => handleArrayChange('features_items', index, { ...feature, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Feature Title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={feature.description}
                    onChange={(e) => handleArrayChange('features_items', index, { ...feature, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Feature description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Icon
                  </label>
                  <select
                    value={feature.icon}
                    onChange={(e) => handleArrayChange('features_items', index, { ...feature, icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="Target">Target</option>
                    <option value="Palette">Palette</option>
                    <option value="Code">Code</option>
                    <option value="Rocket">Rocket</option>
                    <option value="CheckCircle">Check Circle</option>
                    <option value="RefreshCw">Refresh</option>
                    <option value="Headphones">Headphones</option>
                    <option value="Shield">Shield</option>
                    <option value="Star">Star</option>
                    <option value="Download">Download</option>
                    <option value="ShoppingBag">Shopping Bag</option>
                    <option value="Zap">Zap</option>
                    <option value="Globe">Globe</option>
                    <option value="Award">Award</option>
                    <option value="Smartphone">Smartphone</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMoreSection = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">More Section</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              More Title
            </label>
            <input
              type="text"
              value={portfolioData.more_title}
              onChange={(e) => handleInputChange('more_title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ready to Start Your Project?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              More Description
            </label>
            <textarea
              value={portfolioData.more_description}
              onChange={(e) => handleInputChange('more_description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Let's discuss your project requirements and create something amazing together"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Button 1 Text
            </label>
            <input
              type="text"
              value={portfolioData.more_button1_text}
              onChange={(e) => handleInputChange('more_button1_text', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Start Your Project"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Button 1 Link
            </label>
            <input
              type="text"
              value={portfolioData.more_button1_link}
              onChange={(e) => handleInputChange('more_button1_link', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="/contact"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Button 2 Text
            </label>
            <input
              type="text"
              value={portfolioData.more_button2_text}
              onChange={(e) => handleInputChange('more_button2_text', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Get Free Quote"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Button 2 Link
            </label>
            <input
              type="text"
              value={portfolioData.more_button2_link}
              onChange={(e) => handleInputChange('more_button2_link', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="/quote"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'main':
        return renderMainSection();
      case 'stats':
        return renderStatsSection();
      case 'features':
        return renderFeaturesSection();
      case 'more':
        return renderMoreSection();
      default:
        return renderMainSection();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading portfolio page content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Portfolio Page Management</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Manage the content and layout of the Portfolio page
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                  isEditing
                    ? 'bg-gray-600 text-white hover:bg-gray-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isEditing ? <X className="h-4 w-4 mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
                {isEditing ? 'Cancel' : 'Edit'}
              </button>
              {isEditing && (
                <button
                  onClick={savePortfolioData}
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <section.icon className="h-5 w-5 mr-3" />
                  {section.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="space-y-6">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPageManagement; 